import React, { useEffect, useRef, useState } from "react";
import {
  HandCaret,
  HandCheck,
  HandCheckbox,
  HandFrame,
  HandRule,
  HandStrike,
} from "../../../components/hand-drawn/HandDrawn";
import {
  HintSurfaceContext,
  ShortcutKeys,
} from "../../../components/shortcuts/Kbd";
import { TutorialTrackId } from "../../../game/GameState";
import {
  TutorialGoalId,
  TutorialGoalView,
  TutorialStep,
  TutorialStepId,
  tutorialViews,
} from "../../../game/tutorial";
import { dismissTutorial } from "../../../sim/commands/progression-commands";
import { formatCount } from "../../../utils/formatNumber";
import { playMarkerCheck } from "../../../utils/markerSynth";
import { useGame, useShopState } from "../../useShell";
import { useBenchDiveActive } from "../bench/useBenchDive";
import { ManualLink } from "../manual/ManualLink";

/**
 * The tutorials as the character's own to-do list (see game/tutorial.ts):
 * one card per up track — the guided opening, and later the sweeping
 * lesson beneath it — each showing its current goal, its steps as
 * checkboxes that tick off as the shop's state satisfies them. The lists
 * come from the shop's own state, so a card never gets ahead of the
 * player or stuck behind them — it just renders whatever is next.
 *
 * Everything on a card is written rather than typeset: the sheet is
 * `.paper-note`, the hand is `.pencil-hand`, and the rules, boxes,
 * ticks, crossings-out, and key caps are drawn marks (see
 * `hand-drawn/HandDrawn.tsx`). A finished step keeps the same graphite as
 * the rest — the line through it is what says it's done, and the eye is
 * sent to the next thing by the outline in the world, not by fading the
 * list around it.
 *
 * The prose lives here rather than in the step table so instructions can
 * name their keys through the shortcut registry: rebind a key and these
 * sentences follow.
 *
 * A card folds away to a tab — the goal and how many boxes are ticked,
 * on a strip of paper no wider than those words — when the list is in
 * the way of the work, and unfolds again from the same caret. The fold
 * takes the sheet with it, not just the list: a folded card shrinks to
 * fit its heading, at the body's size, with the sheet's margins pulled
 * in, so the column it stands in gives the floor back. Folding is not
 * skipping: the track keeps running
 * and its boxes keep ticking, so the fold is chrome and lives here in
 * component state (kept in localStorage) rather than in the saved
 * progression, where `dismissed` means the player is done with the
 * track for good.
 *
 * The old `components/tutorial/TutorialCard.tsx` over the shell hooks:
 * the state comes from `useShopState()` and the skip goes through the
 * `dismissTutorial` command instead of the action setter.
 */
export const TutorialCards: React.FC = () => {
  const gameState = useShopState();
  const views = tutorialViews(gameState);
  const fold = useCardFold();
  return (
    <>
      {views.map((view) => (
        <TutorialCard key={view.trackId} view={view} {...fold(view.trackId)} />
      ))}
    </>
  );
};

const TutorialCard: React.FC<{
  view: TutorialGoalView;
  folded: boolean;
  onFold: () => void;
}> = ({ view, folded, onFold }) => {
  const game = useGame();

  useMarkerOnCheck(view.goal.id, view.checked);

  // Only the boxes whose thoughts have arrived are on the sheet
  // (view.steps); the goal may hold more that the story hasn't got to.
  const activeIndex = view.checked.findIndex((checked) => !checked);
  const activeStep = activeIndex >= 0 ? view.steps[activeIndex] : null;
  const ticked = view.checked.filter((checked) => checked).length;

  return (
    <HintSurfaceContext.Provider value="hand">
      <section
        className={`paper-note pencil-hand pointer-events-auto relative ${
          folded ? "w-fit max-w-full px-3 py-1" : "space-y-2"
        }`}
        data-testid={`tutorial-card-${view.trackId}`}
        data-folded={folded}
      >
        <header>
          <h3
            className={`pencil-hand-heading leading-tight ${
              folded ? "text-base" : ""
            }`}
          >
            <button
              type="button"
              className="flex w-full items-baseline gap-2 text-left hover:text-ink-black"
              onClick={onFold}
              aria-expanded={!folded}
              data-testid="tutorial-fold"
            >
              <span className="flex-1" data-testid="tutorial-goal">
                {view.goal.title}
              </span>
              {/* Folded, the tally stands in for the boxes it hides. */}
              {folded && (
                <span className="text-[0.8em]">
                  {formatCount(ticked)}/{formatCount(view.checked.length)}
                </span>
              )}
              <HandCaret
                seed={view.goal.id}
                className={`relative top-[0.1em] transition-transform duration-200 ${
                  folded ? "-rotate-90" : ""
                }`}
              />
            </button>
          </h3>
          {!folded && (
            <HandRule seed={view.goal.id} weight={2} className="mt-0.5" />
          )}
        </header>
        {!folded && (
          <>
            <ul className="space-y-1">
              {view.steps.map((step, index) => (
                <ChecklistRow
                  key={step.id}
                  step={step}
                  checked={view.checked[index]}
                />
              ))}
            </ul>
            {activeStep !== null && (
              <div className="space-y-1.5 leading-snug">
                <HandRule
                  seed={`${view.goal.id}-body`}
                  className="opacity-50"
                />
                <StepBody step={activeStep.id} />
              </div>
            )}
            <div className="flex items-center justify-between">
              {/* The sweeping lesson has a manual page behind it; the pointer
                  rides its card the way it rode the old one-time note. */}
              <span>
                {view.trackId === "dust" && <ManualLink article="dust" />}
              </span>
              {/* Written on the sheet like everything else, with a box drawn
                  around it — a printed button would be the one piece of
                  chrome on a page of handwriting. */}
              <button
                className="relative px-3 py-0.5 text-[0.85em] hover:text-ink-black"
                onClick={() => dismissTutorial(game, view.trackId)}
                data-testid="tutorial-skip"
              >
                <HandFrame seed="tutorial-skip" />
                <span className="relative">Skip</span>
              </button>
            </div>
          </>
        )}
      </section>
    </HintSurfaceContext.Provider>
  );
};

const FOLDED_KEY = "woodworking-tycoon-tutorial-folded";

type FoldedTracks = Partial<Record<TutorialTrackId, boolean>>;

function readFolded(): FoldedTracks {
  try {
    const stored = localStorage.getItem(FOLDED_KEY);
    const parsed = stored === null ? null : JSON.parse(stored);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Which cards are folded, and how to fold one — two answers, because the
 * bench asks a different question than the floor.
 *
 * On the floor a fold is a standing choice, remembered across sessions.
 * Leaned over a bench the cards sit on the work surface and eat the
 * gestures aimed at it, so they fold themselves; unfolding one there is
 * a glance at the list, and it folds back up as the lean ends rather
 * than becoming the new preference.
 */
function useCardFold(): (trackId: TutorialTrackId) => {
  folded: boolean;
  onFold: () => void;
} {
  const benchDive = useBenchDiveActive();
  const [folded, setFolded] = useState(readFolded);
  const [readAtBench, setReadAtBench] = useState<FoldedTracks>({});

  useEffect(() => {
    if (!benchDive) setReadAtBench({});
  }, [benchDive]);

  if (benchDive) {
    return (trackId) => ({
      folded: !readAtBench[trackId],
      onFold: () =>
        setReadAtBench((open) => ({ ...open, [trackId]: !open[trackId] })),
    });
  }
  return (trackId) => ({
    folded: folded[trackId] === true,
    onFold: () =>
      setFolded((shut) => {
        const next = { ...shut, [trackId]: shut[trackId] !== true };
        try {
          localStorage.setItem(FOLDED_KEY, JSON.stringify(next));
        } catch {
          // Private-mode storage failures just lose the preference
        }
        return next;
      }),
  });
}

/**
 * Sound the felt tip whenever a box goes from open to ticked. It compares
 * against the previous render's boxes and stays quiet on the first one,
 * so a page load into a half-finished list doesn't tick off everything
 * already done.
 *
 * A new goal counts as a tick too: satisfying a goal's last step moves
 * the card straight on to the next goal, so that box never renders
 * checked — but it was still just crossed off.
 */
function useMarkerOnCheck(
  goalId: TutorialGoalId | null,
  checked: readonly boolean[] | null,
): void {
  const previous = useRef<{
    goalId: TutorialGoalId;
    checked: readonly boolean[];
  } | null>(null);

  useEffect(() => {
    const before = previous.current;
    previous.current =
      goalId === null || checked === null
        ? null
        : { goalId, checked: [...checked] };
    if (before === null || goalId === null || checked === null) return;
    const ticked =
      before.goalId !== goalId ||
      checked.some((now, i) => now && !before.checked[i]);
    if (ticked) {
      playMarkerCheck();
    }
  }, [goalId, checked?.join("")]);
}

const ChecklistRow: React.FC<{
  step: TutorialStep;
  checked: boolean;
}> = ({ step, checked }) => (
  <li
    className="flex items-baseline gap-2 leading-tight"
    data-testid={`tutorial-step-${step.id}`}
    data-checked={checked}
  >
    {/* The box stays; the tick is drawn into it, running past the corner
        the way one does. */}
    <span
      aria-hidden
      className="relative top-[0.15em] flex flex-none items-center justify-center"
    >
      <HandCheckbox seed={step.id} />
      {checked && (
        <HandCheck
          seed={step.id}
          // Not dead centre: a tick sits high and right in its box,
          // because the long stroke is the one that has to clear it.
          className="absolute left-1/2 top-1/2 translate-x-[calc(-50%+0.06em)] translate-y-[calc(-50%-0.13em)]"
        />
      )}
    </span>
    <span className="relative">
      {step.label}
      {checked && <HandStrike seed={`${step.id}-strike`} />}
    </span>
  </li>
);

/**
 * What to do, in plain instruction-manual voice. Every key comes from the
 * registry — never a hard-coded glyph.
 */
const StepBody: React.FC<{ step: TutorialStepId }> = ({ step }) => {
  switch (step) {
    case "scavenge":
      return (
        <p>
          Wood costs money, and you have none. Walk out the garage door to the
          truck's cab, press <ShortcutKeys shortcut="pick-up" />, and pick the
          scavenging trip. Pallets are free for the hauling.
        </p>
      );
    case "headHome":
      return (
        <p>
          The pallet rides along in the truck. Pick <em>Back to the shop</em> in
          the cab, and it lands in the bed.
        </p>
      );
    case "unloadPallet":
      return (
        <p>
          Stand at the tailgate and take the pallet out with{" "}
          <ShortcutKeys shortcut="pick-up" />, carry it to the workbench, and
          set it down there with <ShortcutKeys shortcut="put-down" />.
        </p>
      );
    case "dismantle":
      return (
        <p>
          Open the bench with <ShortcutKeys shortcut="pick-up" /> and pry the
          marked nails with the mouse. Every board comes free one pull at a
          time, and the nails go back in your tin.
        </p>
      );
    case "layOutShelf":
      return (
        <p>
          You have the wood for a shelf: two thick stringers and three deck
          boards. At the bench, click the pile of plans under the tool rail and
          pull <em>Rustic Pallet Shelf</em>, then drag each board onto its
          outline.
        </p>
      );
    case "buildShelf":
      return (
        <p>
          Every board is on its outline. Press each marked nail to drive it
          home; the last one makes the boards a shelf.
        </p>
      );
    case "setUpStand":
      return (
        <p>
          Walk out the garage door and down the driveway to the grass by the
          road, on the far side of the truck. Press{" "}
          <ShortcutKeys shortcut="pick-up" /> there to set the table up and
          plant the sign.
        </p>
      );
    case "sellShelf":
      return (
        <p>
          The stand is where work gets sold. Pick the shelf up with{" "}
          <ShortcutKeys shortcut="pick-up" />, carry it down, and set it out
          with <ShortcutKeys shortcut="put-down" />.
        </p>
      );
    case "firstSale":
      return (
        <p>
          Someone walking by will stop at the stand and buy the shelf. Buyers
          come by in daylight — if the day is already over, drive home and
          sleep, and the first sale comes in the morning.
        </p>
      );
    case "learnSkill":
      return (
        <p>
          Building the shelf earned your first level, and a level is a skill
          point. Open the journal with <ShortcutKeys shortcut="open-journal" />{" "}
          and learn <em>Rustic Projects</em> — it teaches the birdhouse.
        </p>
      );
    case "goToStore":
      return (
        <p>
          The birdhouse's parts have to be cut to length, and that takes a saw.
          Press <ShortcutKeys shortcut="pick-up" /> at the truck's cab and pick
          the Orange Box.
        </p>
      );
    case "addSawToCart":
      return (
        <p>
          The hand saw hangs on the tool wall. Add it to the cart — the shelf
          money covers it.
        </p>
      );
    case "checkOut":
      return (
        <p>
          The register takes what the cart holds, and everything you bought
          rides home in the truck's bed.
        </p>
      );
    case "gatherWood":
      return (
        <p>
          The birdhouse takes two whole pallet boards. If the shop is short,
          take the truck out for another pallet and pry it apart.
        </p>
      );
    case "mountSaw":
      return (
        <p>
          A hand tool only works where it's hung. With the saw in your arms,
          stand at the workbench and open it with{" "}
          <ShortcutKeys shortcut="pick-up" />. The saw sits in the tray at the
          bottom right; drag it onto an empty hook on the rail.
        </p>
      );
    case "cutParts":
      return (
        <p>
          Cut the birdhouse's parts at the bench: two 12-inch fronts, each with
          one end cut at the 45° stop, a 12-inch roof, a 12-inch floor, and two
          6-inch sides. The plan <em>Birdhouse</em> lists each part as you go.
        </p>
      );
    case "assembleBirdhouse":
      return (
        <p>
          Load the parts at the bench, choose the plan <em>Birdhouse</em>, then
          set each piece on the drawing and nail it down.
        </p>
      );
    case "earnSavings":
      return (
        <p>
          Anything finished sells at the stand. Keep scavenging, building, and
          setting work out — the savings buy the first real saw.
        </p>
      );
    case "buyMachine":
      return (
        <p>
          The Orange Box sells machines in full-size displays. Press{" "}
          <ShortcutKeys shortcut="pick-up" /> at the truck's cab, drive over,
          put the miter saw on the cart, and check out. It rides home crated in
          the truck's bed.
        </p>
      );
    case "placeMachine":
      return (
        <p>
          Stand at the tailgate and press{" "}
          <ShortcutKeys shortcut="carry-machine" /> to lift the crate onto your
          shoulders. Carry it inside, turn it with{" "}
          <ShortcutKeys shortcut="carry-rotate" />, and press{" "}
          <ShortcutKeys shortcut="carry-machine" /> again to set it down where
          the outline shows it fits.
        </p>
      );
    case "driveHome":
      return (
        <p>
          The day ends at the truck. Press <ShortcutKeys shortcut="pick-up" />{" "}
          at the cab and pick the drive home. Anything still curing is dry by
          morning, and the truck is back in the driveway.
        </p>
      );
    case "getBroom":
      return (
        <p>
          Left on the floor, sawdust slows your machines and your feet. The
          Orange Box sells a broom — press <ShortcutKeys shortcut="pick-up" />{" "}
          at the truck's cab, drive over, and take one off the tool wall.
        </p>
      );
    case "sweepUp":
      return (
        <p>
          Pick the broom up with <ShortcutKeys shortcut="pick-up" />, then hold{" "}
          <ShortcutKeys shortcut="operate-machine" /> and walk to sweep the dust
          into the dustpan.
        </p>
      );
    case "emptyPan":
      return (
        <p>
          The pan rides on the broom. Stand at the garbage can and hold{" "}
          <ShortcutKeys shortcut="operate-machine" /> to pour it out.
        </p>
      );
  }
};
